import styled from 'styled-components';
import Button from '../Button/button';
import COLORS from '../../style/universal.color';

const { linkBlue, mlbGrey2, mlbGrey6, mlbGrey7, mlbWhite } = COLORS;

const shadowWidth = '60px';
const buttonsSpacing = 8;

export const CONTROLLED_OVERFLOW = 'controlled-overflow';

interface SideButtonProps {
  dark: boolean;
  mobileBreakpoint?: string;
  buttonHeight: number;
  buttonsOnSide: boolean;
  hideOffScreen: boolean;
  buttonSideMargin: number;
}

// The buttons that appear on the left and right of the overflow container.
// These are styled off of the react library's Button
export const LeftButton = styled(Button)<SideButtonProps>`
  position: absolute;
  ${({ buttonsOnSide, hideOffScreen, buttonHeight, buttonSideMargin }) =>
    buttonsOnSide
      ? `right: ${!hideOffScreen ? `${buttonHeight + buttonSideMargin + buttonsSpacing}px` : `${(buttonHeight + 2) * -1}px`};`
      : 'left: 10px;'};
  top: 50%;
  transform: translateY(-50%);
  z-index: 110;
  box-shadow: 0px 1px 2px 1px rgba(0, 0, 0, 0.15);
  width: ${({ buttonHeight }) => buttonHeight}px;
  height: ${({ buttonHeight }) => buttonHeight}px;
  padding-left: 0;
  padding-right: 2px;
  background-color: ${({ dark }) => (dark ? mlbGrey6 : mlbWhite)};
  display: flex;
  align-items: center;
  svg {
    padding-top: 0px;
    margin: 0 auto;
    width: ${({ buttonHeight }) => buttonHeight / 2}px;
    height: ${({ buttonHeight }) => buttonHeight / 3}px;
  }
  &&& {
    border-radius: 50%;
    box-sizing: border-box;
  }

  @media (pointer: coarse) {
    display: none;
  }

  @media (pointer: fine) {
    display: flex;
  }

  &:hover {
    &:not(:disabled) {
      background-color: ${({ dark }) => (dark ? mlbGrey7 : mlbGrey2)};
    }
  }
`;
export const RightButton = styled(LeftButton)`
  right: ${({ hideOffScreen, buttonsOnSide, buttonHeight, buttonSideMargin }) =>
    buttonsOnSide
      ? `${!hideOffScreen ? buttonSideMargin : `${(buttonHeight * 2 + (buttonSideMargin + buttonsSpacing)) * -1}`}px`
      : '10px'};
  left: initial;
  padding-left: 2px;
  padding-right: 0px;
`;

interface ShadowProps {
  dark: boolean;
  mobileBreakpoint?: string;
  show: boolean;
  gradientColor: string;
}

// The gradient "shadows" that appear on the left and the right when not at
// an end of the container (and it overflows).
// Transitions from view when 'show' variable changes.
export const LeftShadow = styled.div.attrs<{
  'data-mlb-test'?: string;
  'data-testid'?: string;
}>({
  'data-mlb-test': `${CONTROLLED_OVERFLOW}_left-shadow`,
  'data-testid': `${CONTROLLED_OVERFLOW}_left-shadow`,
})<ShadowProps>`
  height: 100%;
  z-index: 100;
  background-image: linear-gradient(to right, ${({ gradientColor }) => gradientColor}, rgba(0, 0, 0, 0));
  transform: ${({ show }) => (show ? 'translate(0px, 0px);' : `translate(-${shadowWidth}, 0px)`)};
  width: ${shadowWidth};
  position: absolute;
  top: 0px;
  left: 0px;
  transition: transform 0.25s ease-out;
  @media (max-width: ${({ mobileBreakpoint }) => mobileBreakpoint}) {
    display: none;
  }
`;
export const RightShadow = styled(LeftShadow).attrs<{
  'data-mlb-test'?: string;
  'data-testid'?: string;
}>({
  'data-mlb-test': `${CONTROLLED_OVERFLOW}_right-shadow`,
  'data-testid': `${CONTROLLED_OVERFLOW}_right-shadow`,
})`
  right: 0px;
  left: initial;
  background-image: linear-gradient(to left, ${({ gradientColor }) => gradientColor}, rgba(0, 0, 0, 0));
  transform: ${({ show }) => (show ? 'translate(0px, 0px)' : `translate(${shadowWidth}, 0px)`)};
`;

interface InnerWrapperProps {
  pauseScrollSnapping: boolean;
  numberChildren: number;
  dark: boolean;
  buttonsOnSide: boolean;
  buttonHeight: number;
  buttonSideMargin: number;
  childWidth: number;
  'data-mlb-test'?: string;
  'data-testid'?: string;
}

// The inner wrapper that sets the scroll snapping and where to align
// with the inner children.
export const InnerWrapper = styled.div.attrs<InnerWrapperProps>(() => ({
  'data-mlb-test': `${CONTROLLED_OVERFLOW}_inner-wrapper`,
  'data-testid': `${CONTROLLED_OVERFLOW}_inner-wrapper`,
}))`
  overflow: auto;
  justify-content: flex-start;
  -ms-overflow-style: none; /* IE 11 */
  scrollbar-width: none; /* Firefox 64 */
  &::-webkit-scrollbar {
    display: none;
  }
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  align-items: flex-start;
  margin-right: ${({ buttonsOnSide, buttonHeight, buttonSideMargin }) =>
    buttonsOnSide ? `${buttonHeight * 2 + buttonsSpacing + buttonSideMargin * 2}px` : '0px'};

  @media (pointer: coarse) {
    margin-right: 0;
  }

  @media (pointer: fine) {
    margin-right: ${({ buttonsOnSide, buttonHeight, buttonSideMargin }) =>
      buttonsOnSide ? `${buttonHeight * 2 + buttonsSpacing + buttonSideMargin * 2}px` : '0px'};
  }

  min-width: ${({ buttonsOnSide, buttonHeight, buttonSideMargin }) =>
    buttonsOnSide ? `calc(100% - ${buttonHeight * 2 + buttonsSpacing + buttonSideMargin * 2}px)` : 'calc(100% - 0px)'};

  transition-duration: 0.25s;
  transition-property: margin-right;
  ${({ pauseScrollSnapping }) => (pauseScrollSnapping ? '' : 'scroll-snap-type: x mandatory;')}

  > div {
    display: inline-block;
    scroll-snap-align: center;
    flex-shrink: 0;
    &:first-child {
      scroll-snap-align: start;
    }
  }
`;

interface OuterWrapperProps {
  'data-mlb-test'?: string;
  'data-testid'?: string;
}

// The wrapper around the scrolling chamber, the side buttons, and the shadows.
export const OuterWrapper = styled.div.attrs<OuterWrapperProps>(() => ({
  'data-mlb-test': `${CONTROLLED_OVERFLOW}_outer-wrapper`,
  'data-testid': `${CONTROLLED_OVERFLOW}_outer-wrapper`,
}))`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: left;
  flex-direction: row;
  -webkit-overflow-scrolling: touch;
  overflow: hidden;
  flex: 1 1 auto;
`;

interface PaginationWrapperProps {
  mobileBreakpoint?: string;
  'data-mlb-test'?: string;
  'data-testid'?: string;
}

export const PaginationWrapper = styled.div.attrs<PaginationWrapperProps>(() => ({
  'data-mlb-test': `${CONTROLLED_OVERFLOW}_pagination-wrapper`,
  'data-testid': `${CONTROLLED_OVERFLOW}_pagination-wrapper`,
}))`
  text-align: center;
  @media (min-width: ${({ mobileBreakpoint }) => mobileBreakpoint}) {
    display: none;
  }
  position: absolute;
  width: 100%;
  bottom: 0px;
  justify-content: center;
`;

export const PaginationButton = styled(Button)`
  cursor: pointer;
  height: 8px;
  width: 8px;
  border-radius: 50%;
  padding: 0px;
  box-shadow: 0px;
  border: 0px;
  margin: 0px 4px;
  background-color: ${({ active }) => (active ? linkBlue : '#e9f4ff')};
`;

interface EndInsertProps {
  insertWidth: number;
}

export const EndInsert = styled.div<EndInsertProps>`
  width: ${({ insertWidth }) => insertWidth}px;
  height: 100%;
`;
